// Kom igång: byrån väljer en startmall för tjänstekatalogen (designen
// Admin-Start). Valet skapar ett utkast, som byrån ändrar i tjänstekatalogen
// innan det publiceras. Bara ägaren kan välja. En byrå vars senaste version
// saknar tjänster, till exempel efter "Börja från tomt", får också välja. Är
// den ett utkast fylls det med mallen, annars blir mallen ett nytt utkast.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useId, useState, type FormEvent } from 'react';
import { Link, Navigate, useNavigate } from 'react-router';
import { ApiFel, data } from '../api/client.js';
import { nycklar, usePrismodeller, useStartmallar } from '../api/queries.js';
import { useAuth, useMe } from '../auth/auth.js';
import { antalTjanster, forstaPris, TOM_KATALOG, type Prisrad } from '../katalog/sammanfattning.js';
import { Marke } from '../layout/ikoner.js';

const TOM = 'tom';

const STEG = ['Byrå', 'Priser', 'Offertmall', 'Formulär'] as const;
const AKTIVT_STEG = 1;

function Steg() {
  return (
    <ol className="steg" aria-label="Steg">
      {STEG.map((namn, i) => (
        <li
          key={namn}
          className={i < AKTIVT_STEG ? 'klart' : i === AKTIVT_STEG ? 'aktivt' : undefined}
          aria-current={i === AKTIVT_STEG ? 'step' : undefined}
        >
          <span className="punkt" aria-hidden="true">
            {i < AKTIVT_STEG ? (
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="m5 12 5 5 9-10" />
              </svg>
            ) : (
              i + 1
            )}
          </span>
          {namn}
          {i < AKTIVT_STEG && <span className="visuellt-dold"> (klart)</span>}
        </li>
      ))}
    </ol>
  );
}

interface Val {
  key: string;
  namn: string;
  beskrivning: string;
  antal: string;
  pris: Prisrad | null;
}

function Mallkort({ val, vald, onVal }: { val: Val; vald: boolean; onVal: () => void }) {
  const id = useId();
  return (
    <label className={`mall${vald ? ' vald' : ''}`} htmlFor={id}>
      <input
        id={id}
        type="radio"
        name="startmall"
        value={val.key}
        checked={vald}
        onChange={onVal}
      />
      <span className="mall-namn">{val.namn}</span>
      <span className="mall-text">{val.beskrivning}</span>
      <span className="mall-fot">
        <span>{val.antal}</span>
        {val.pris ? (
          <span>
            {val.pris.text} <span className="mono">{val.pris.belopp}</span>
          </span>
        ) : (
          <span>Ni lägger upp tjänsterna själva</span>
        )}
      </span>
    </label>
  );
}

export function KomIgang() {
  const me = useMe();
  const { api } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const mallar = useStartmallar();
  const modeller = usePrismodeller();
  const [valt, setValt] = useState<string | null>(null);
  // Den senaste versionen, nyast först. Saknar den tjänster får byrån välja mall.
  const senaste = modeller.data?.[0] ?? null;
  const senasteDetalj = useQuery({
    queryKey: ['prismodell', senaste?.id],
    enabled: senaste !== null,
    queryFn: async () =>
      data(
        await api.GET('/v1/admin/pricing-models/{id}', {
          params: { path: { id: senaste?.id ?? '' } },
        }),
      ),
  });
  const tomSenaste =
    senaste && senasteDetalj.data?.definition.services.length === 0 ? senaste : null;
  const tomtUtkast = tomSenaste?.status === 'draft' ? tomSenaste : null;

  useEffect(() => {
    document.title = 'Kom igång – LeadLedger';
  }, []);

  const val: Val[] = [
    ...(mallar.data ?? []).map((m) => ({
      key: m.key,
      namn: m.name,
      beskrivning: m.description,
      antal: antalTjanster(m.definition.services.length),
      pris: forstaPris(m.definition),
    })),
    {
      key: TOM,
      namn: 'Börja från tomt',
      beskrivning:
        'Lägg upp alla tjänster och priser själva. Passar om ni redan har en färdig prislista att utgå från.',
      antal: antalTjanster(0),
      pris: null,
    },
  ];
  const markerad = val.find((v) => v.key === (valt ?? val[0]?.key)) ?? val[0];

  const skapa = useMutation({
    mutationFn: async (key: string) => {
      if (tomtUtkast) {
        const mall = mallar.data?.find((m) => m.key === key);
        return data(
          await api.PUT('/v1/admin/pricing-models/{id}', {
            params: { path: { id: tomtUtkast.id } },
            body: { definition: mall ? mall.definition : TOM_KATALOG },
          }),
        );
      }
      return data(
        await api.POST('/v1/admin/pricing-models', {
          body: key === TOM ? { definition: TOM_KATALOG } : { templateKey: key },
        }),
      );
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: nycklar.prismodeller });
      void navigate('/katalog');
    },
    onError: async (err) => {
      // Någon annan på byrån hann före: utkastet finns redan.
      if (err instanceof ApiFel && err.status === 409) {
        await queryClient.invalidateQueries({ queryKey: nycklar.prismodeller });
        void navigate('/katalog');
      }
    },
  });

  // Byrån har redan en katalog med tjänster. Då finns inget att välja här.
  const vantarPaUtkast = senaste !== null && senasteDetalj.isPending;
  if (
    modeller.data &&
    modeller.data.length > 0 &&
    !vantarPaUtkast &&
    !tomSenaste &&
    !skapa.isPending &&
    !skapa.isSuccess
  ) {
    return <Navigate to="/katalog" replace />;
  }

  const agare = me.user.role === 'owner';
  const laddar = mallar.isPending || modeller.isPending || vantarPaUtkast;
  const fel = mallar.isError || modeller.isError;

  function fortsatt(e: FormEvent) {
    e.preventDefault();
    if (markerad) skapa.mutate(markerad.key);
  }

  return (
    <div className="kom-igang">
      <header className="kom-igang-huvud">
        <span className="marke">
          <Marke />
          LeadLedger
        </span>
        <Steg />
        <Link to="/">Hoppa över tills vidare</Link>
      </header>
      <main id="innehall" className="kom-igang-sida">
        <div>
          <h1>Välj en startmall för era priser</h1>
          <p className="ingress">
            Mallen fyller er tjänstekatalog med förslag på tjänster och priser. Ni ändrar allt i
            nästa steg, och inget visas för kunder förrän ni publicerar.
          </p>
        </div>
        {!agare && (
          <div className="kort notis" role="note">
            Bara byråns ägare kan välja startmall. Be ägaren att logga in och välja.
          </div>
        )}
        {laddar ? (
          <p role="status">Hämtar startmallarna …</p>
        ) : fel ? (
          <div className="meddelande" role="alert">
            Startmallarna gick inte att hämta. Ladda om sidan och försök igen.
          </div>
        ) : (
          <form onSubmit={fortsatt}>
            <fieldset className="mallar" disabled={!agare || skapa.isPending}>
              <legend className="visuellt-dold">Startmall</legend>
              {val.map((v) => (
                <Mallkort key={v.key} val={v} vald={v === markerad} onVal={() => setValt(v.key)} />
              ))}
            </fieldset>
            {skapa.isError && !(skapa.error instanceof ApiFel && skapa.error.status === 409) && (
              <div className="meddelande" role="alert">
                Det gick inte att skapa tjänstekatalogen. Försök igen om en stund.
              </div>
            )}
            {agare && markerad && (
              <div className="kom-igang-fot">
                <button type="submit" className="btn btn-p" disabled={skapa.isPending}>
                  {skapa.isPending
                    ? 'Skapar tjänstekatalogen …'
                    : markerad.key === TOM
                      ? 'Fortsätt med en tom katalog'
                      : `Fortsätt med ${markerad.namn}`}
                </button>
              </div>
            )}
          </form>
        )}
      </main>
    </div>
  );
}
